/**
 * Input Validation Utility
 * 
 * Provides centralized input validation for all features to ensure:
 * - No empty text is sent to API
 * - Text meets minimum length requirements
 * - Text doesn't exceed maximum length limits
 * - Consistent validation messages across features
 * 
 * Issue #8: Missing Input Validation
 */

export interface ValidationResult {
  isValid: boolean;
  error?: string;
  warning?: string;
}

export interface ValidationConfig {
  minLength?: number;
  maxLength?: number;
  allowEmpty?: boolean;
  customValidation?: (text: string) => ValidationResult;
}

/**
 * Default validation configuration
 */
export const DEFAULT_VALIDATION_CONFIG: Required<Omit<ValidationConfig, 'customValidation'>> = {
  minLength: 10,
  maxLength: 100000, // 100k characters
  allowEmpty: false,
};

/**
 * Validates text input against specified rules
 * 
 * @param text - The text to validate
 * @param config - Optional validation configuration
 * @returns ValidationResult with isValid flag and optional error/warning messages
 * 
 * @example
 * ```typescript
 * const result = validateTextInput(selectedText);
 * if (!result.isValid) {
 *   setError(result.error);
 *   return;
 * }
 * ```
 */
export const validateTextInput = (
  text: string | null | undefined,
  config: ValidationConfig = {}
): ValidationResult => {
  const {
    minLength = DEFAULT_VALIDATION_CONFIG.minLength,
    maxLength = DEFAULT_VALIDATION_CONFIG.maxLength,
    allowEmpty = DEFAULT_VALIDATION_CONFIG.allowEmpty,
    customValidation,
  } = config;

  // Check for null or undefined
  if (text === null || text === undefined) {
    return {
      isValid: false,
      error: 'No text provided. Please select or enter some text.',
    };
  }

  // Coerce to string to handle Word API proxy objects
  const safeText = typeof text === 'string' ? text : String(text);

  // Trim whitespace for validation
  const trimmedText = safeText.trim();

  // Check for empty text
  if (!allowEmpty && trimmedText.length === 0) {
    return {
      isValid: false,
      error: 'Please select or enter some text before proceeding.',
    };
  }

  // Check minimum length
  if (trimmedText.length > 0 && trimmedText.length < minLength) {
    return {
      isValid: false,
      error: `Text is too short. Please provide at least ${minLength} characters. Current length: ${trimmedText.length} characters.`,
    };
  }

  // Check maximum length
  if (trimmedText.length > maxLength) {
    return {
      isValid: false,
      error: `Text is too long. Maximum allowed length is ${maxLength.toLocaleString()} characters. Current length: ${trimmedText.length.toLocaleString()} characters. Please select a smaller portion of text.`,
    };
  }

  // Warning for large text (80% of max)
  const warningThreshold = maxLength * 0.8;
  let warning: string | undefined;
  if (trimmedText.length > warningThreshold) {
    warning = `Large text detected (${trimmedText.length.toLocaleString()} characters). Processing may take longer than usual.`;
  }

  // Custom validation if provided
  if (customValidation) {
    const customResult = customValidation(trimmedText);
    if (!customResult.isValid) {
      return customResult;
    }
    // Merge warnings
    if (customResult.warning) {
      warning = warning ? `${warning} ${customResult.warning}` : customResult.warning;
    }
  }

  return {
    isValid: true,
    warning,
  };
};

/**
 * Validates that text contains actual content (not just whitespace or special characters)
 * 
 * @param text - The text to validate
 * @returns ValidationResult
 */
export const validateTextContent = (text: string): ValidationResult => {
  const safeText = typeof text === 'string' ? text : String(text);
  const trimmedText = safeText.trim();

  // Check if text contains at least some alphanumeric characters
  const hasContent = /[a-zA-Z0-9]/.test(trimmedText);

  if (!hasContent) {
    return {
      isValid: false,
      error: 'Text must contain actual content (letters or numbers), not just special characters or whitespace.',
    };
  }

  return { isValid: true };
};

/**
 * Validates text for specific feature requirements
 */
export const featureValidators = {
  /**
   * Validation for AI Edit feature
   * Requires both text and prompt
   */
  aiEdit: (text: string, prompt: string): ValidationResult => {
    const textValidation = validateTextInput(text, { minLength: 10, maxLength: 50000 });
    if (!textValidation.isValid) {
      return textValidation;
    }

    const promptValidation = validateTextInput(prompt, { minLength: 5, maxLength: 500 });
    if (!promptValidation.isValid) {
      return {
        isValid: false,
        error: `Invalid prompt: ${promptValidation.error}`,
      };
    }

    return { isValid: true };
  },

  /**
   * Validation for Content Improvement feature
   * Requires meaningful text content
   */
  contentImprovement: (text: string): ValidationResult => {
    const basicValidation = validateTextInput(text, { minLength: 20, maxLength: 50000 });
    if (!basicValidation.isValid) {
      return basicValidation;
    }

    return validateTextContent(text);
  },

  /**
   * Validation for Content Suggestions feature
   * Requires context for suggestions
   */
  contentSuggestions: (text: string): ValidationResult => {
    const basicValidation = validateTextInput(text, { minLength: 50, maxLength: 100000 });
    if (!basicValidation.isValid) {
      return basicValidation;
    }

    // Check for minimum word count (at least 10 words for meaningful suggestions)
    const wordCount = text.trim().split(/\s+/).length;
    if (wordCount < 10) {
      return {
        isValid: false,
        error: `Text is too short for meaningful suggestions. Please provide at least 10 words. Current word count: ${wordCount} words.`,
      };
    }

    return { isValid: true };
  },

  /**
   * Validation for Translation feature
   */
  translation: (text: string): ValidationResult => {
    return validateTextInput(text, { minLength: 1, maxLength: 50000 });
  },

  /**
   * Validation for Proofreading feature
   */
  proofreading: (text: string): ValidationResult => {
    const basicValidation = validateTextInput(text, { minLength: 10, maxLength: 100000 });
    if (!basicValidation.isValid) {
      return basicValidation;
    }

    return validateTextContent(text);
  },

  /**
   * Validation for ChatBot feature
   */
  chatBot: (message: string): ValidationResult => {
    return validateTextInput(message, { minLength: 1, maxLength: 2000 });
  },

  /**
   * Validation for Document Analysis features (Compliance, Risk, etc.)
   */
  documentAnalysis: (text: string): ValidationResult => {
    return validateTextInput(text, { minLength: 50, maxLength: 100000 });
  },
};

/**
 * Helper function to get character count with formatting
 */
export const getCharacterCount = (text: string): string => {
  const count = text.trim().length;
  return count.toLocaleString();
};

/**
 * Helper function to get word count
 */
export const getWordCount = (text: string): number => {
  return text.trim().split(/\s+/).filter(word => word.length > 0).length;
};

/**
 * Helper function to check if text is within safe limits
 */
export const isWithinSafeLimits = (text: string, maxLength: number = DEFAULT_VALIDATION_CONFIG.maxLength): boolean => {
  return text.trim().length <= maxLength;
};

/**
 * Helper function to truncate text to safe length
 */
export const truncateToSafeLength = (text: string, maxLength: number = DEFAULT_VALIDATION_CONFIG.maxLength): string => {
  const trimmed = text.trim();
  if (trimmed.length <= maxLength) {
    return trimmed;
  }
  return trimmed.substring(0, maxLength);
};

